const ATTENDANT_CACHE_KEY = 'attendant';
const ATTENDANT_CACHE_TS_KEY = 'attendant_cached_at';
const ATTENDANT_CACHE_TTL_MS = 1 * 60 * 1000; // 1 minutos

const CONTACTS_CACHE_KEY = 'clients_contacts';
const CONTACTS_CACHE_TS_KEY = 'clients_contacts_cached_at';
const CONTACTS_CACHE_TTL_MS = 1 * 60 * 1000; // 1 minutos
export const CONTACTS_CACHE_ALL_KEY = 'all';
// Mantém o cache de contatos enxuto para não estourar o localStorage
const CONTACTS_CACHE_MAX_COMPANIES = 20;
const CONTACTS_CACHE_MAX_CONTACTS_PER_COMPANY = 50;
const CONTACTS_CACHE_MAX_TOTAL_CONTACTS = 400;

const CLIENTS_CACHE_KEY = 'clients';
const CLIENTS_CACHE_TS_KEY = 'clients_cached_at';
const CLIENTS_CACHE_TTL_MS = 1 * 60 * 1000; // 1 minutos

const CHAT_SLOTS_PREFIX = 'chat_slots_cache_';
const CONTACTS_CACHE_MAX_STRING_LENGTH = 2000;
const CONTACTS_CACHE_SKIP_KEY_REGEX = /(photo|image|avatar|picture|thumb|document|file|attachment|base64)/i;
const BUILD_CACHE_KEY = 'blz_app_build_id';
const SENSITIVE_REMOTE_MEDIA_REGEX = /^(data:|blob:|file:|https?:\/\/)/i;
const LEGACY_BROKEN_CONTACT_MEDIA_REGEX = /^\/?media\/contact_[^/]+\.(?:jpe?g|png|webp|gif|bmp)$/i;

const toNumber = (value) => {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const isFresh = (cachedAt, ttl) => {
  if (!Number.isFinite(ttl) || ttl <= 0) {
    return true;
  }
  if (cachedAt === null) {
    return false;
  }
  return Date.now() - cachedAt <= ttl;
};

const clearCacheStorage = async () => {
  if (typeof caches === 'undefined' || typeof caches.keys !== 'function') {
    return;
  }
  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  } catch (err) {
    console.warn('Não foi possível limpar CacheStorage:', err);
  }
};

export const clearBrowserCaches = () => {
  // Dispara a limpeza de CacheStorage de forma assíncrona e resiliente
  try {
    void clearCacheStorage();
  } catch {
    // ignore
  }
};

const safeParseJSON = (value, fallback = null) => {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const safeSetItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (err) {
    console.warn(`Não foi possível salvar "${key}" no localStorage:`, err);
    return false;
  }
};

const sanitizeContactEntry = (contact) => {
  if (!contact || typeof contact !== 'object') return null;
  const safe = {};

  Object.keys(contact).forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(contact, key)) return;
    const value = contact[key];
    if (value === undefined || value === null) return;

    const type = typeof value;
    if (type === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return;
      if (trimmed.length > CONTACTS_CACHE_MAX_STRING_LENGTH) return;
      if (CONTACTS_CACHE_SKIP_KEY_REGEX.test(key)) {
        const keyLower = key.toLowerCase();
        const canCachePhotograph =
          keyLower === 'photograph' &&
          !LEGACY_BROKEN_CONTACT_MEDIA_REGEX.test(trimmed) &&
          !SENSITIVE_REMOTE_MEDIA_REGEX.test(trimmed);
        if (!canCachePhotograph) return;
      }
      safe[key] = trimmed;
      return;
    }

    if (type === 'number' || type === 'boolean') {
      safe[key] = value;
    }
  });

  if (!safe.whatsapp_number && !safe.contact_phone && !safe.phone_number) {
    const fallbackNumber =
      contact.whatsapp_number ||
      contact.contact_phone ||
      contact.phone_number ||
      contact.whatsapp ||
      contact.phone ||
      null;
    if (typeof fallbackNumber === 'string') {
      const trimmed = fallbackNumber.trim();
      if (trimmed && trimmed.length <= CONTACTS_CACHE_MAX_STRING_LENGTH) {
        safe.whatsapp_number = trimmed;
      }
    }
  }

  if (!safe.name && !safe.contact_name && !safe.whatsapp_name) {
    const fallbackName =
      contact.name ||
      contact.contact_name ||
      contact.whatsapp_name ||
      contact.customer_name ||
      contact.client_name ||
      null;
    if (typeof fallbackName === 'string') {
      const trimmed = fallbackName.trim();
      if (trimmed && trimmed.length <= CONTACTS_CACHE_MAX_STRING_LENGTH) {
        safe.name = trimmed;
      }
    }
  }

  return Object.keys(safe).length > 0 ? safe : null;
};

const limitContactsCacheEntries = (map) => {
  if (!map || typeof map !== 'object') return {};
  const entries = Object.entries(map).filter(
    ([, list]) => Array.isArray(list) && list.length > 0
  );
  if (entries.length === 0) return {};

  const hasCompanyLimit =
    Number.isFinite(CONTACTS_CACHE_MAX_COMPANIES) &&
    CONTACTS_CACHE_MAX_COMPANIES > 0;
  const hasTotalLimit =
    Number.isFinite(CONTACTS_CACHE_MAX_TOTAL_CONTACTS) &&
    CONTACTS_CACHE_MAX_TOTAL_CONTACTS > 0;

  const picked = [];
  let totalContacts = 0;

  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const [companyId, contacts] = entries[i];
    if (!Array.isArray(contacts) || contacts.length === 0) continue;

    let trimmed = contacts;
    if (
      Number.isFinite(CONTACTS_CACHE_MAX_CONTACTS_PER_COMPANY) &&
      CONTACTS_CACHE_MAX_CONTACTS_PER_COMPANY > 0 &&
      trimmed.length > CONTACTS_CACHE_MAX_CONTACTS_PER_COMPANY
    ) {
      trimmed = trimmed.slice(0, CONTACTS_CACHE_MAX_CONTACTS_PER_COMPANY);
    }

    if (hasTotalLimit) {
      const remaining = CONTACTS_CACHE_MAX_TOTAL_CONTACTS - totalContacts;
      if (remaining <= 0) break;
      if (trimmed.length > remaining) {
        trimmed = trimmed.slice(0, remaining);
      }
    }

    if (!trimmed.length) continue;

    picked.push([companyId, trimmed]);
    totalContacts += trimmed.length;

    const reachedCompanyLimit = hasCompanyLimit && picked.length >= CONTACTS_CACHE_MAX_COMPANIES;
    const reachedTotalLimit = hasTotalLimit && totalContacts >= CONTACTS_CACHE_MAX_TOTAL_CONTACTS;
    if (reachedCompanyLimit || reachedTotalLimit) {
      break;
    }
  }

  picked.reverse();
  return picked.reduce((acc, [companyId, contacts]) => {
    acc[companyId] = contacts;
    return acc;
  }, {});
};

const sanitizeContactsMap = (value) => {
  if (!value || typeof value !== 'object') return {};

  const safeMap = {};
  Object.keys(value).forEach((rawKey) => {
    const contactsList = Array.isArray(value[rawKey]) ? value[rawKey] : [];
    const sanitizedList = contactsList
      .map((contact) => sanitizeContactEntry(contact))
      .filter(Boolean);
    if (sanitizedList.length > 0) {
      safeMap[rawKey] = sanitizedList;
    }
  });

  return limitContactsCacheEntries(safeMap);
};

export const sanitizeContactsList = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((contact) => sanitizeContactEntry(contact))
    .filter(Boolean);
};

export const flattenContactsCacheMap = (map) => {
  if (!map || typeof map !== 'object') return [];

  const flattened = [];
  const seen = new Set();

  Object.values(map).forEach((contactsList) => {
    const safeList = Array.isArray(contactsList) ? contactsList : [];
    safeList.forEach((contact, index) => {
      const safeContact = sanitizeContactEntry(contact);
      if (!safeContact) return;

      const dedupKey =
        safeContact.id ??
        safeContact.whatsapp_jid ??
        safeContact.whatsapp_lid ??
        safeContact.whatsapp_number ??
        safeContact.contact_phone ??
        `${index}:${safeContact.name || ''}`;

      const normalizedKey = String(dedupKey);
      if (seen.has(normalizedKey)) return;
      seen.add(normalizedKey);
      flattened.push(safeContact);
    });
  });

  return flattened;
};

// Retorna chave com escopo por empresa (se houver company_id)
const scopedKey = (baseKey) => {
  try {
    const companyId = localStorage.getItem('company_id');
    if (!companyId) return baseKey;
    return `${baseKey}::${companyId}`;
  } catch {
    return baseKey;
  }
};

export const readAttendantCache = () => null;
export const writeAttendantCache = () => {};
export const clearAttendantCache = () => {};

export const readContactsCache = () => {
  // Tenta ler a versão com escopo primeiro; se não houver, tenta legado
  const key = scopedKey(CONTACTS_CACHE_KEY);
  const tsKey = scopedKey(CONTACTS_CACHE_TS_KEY);

  let raw = localStorage.getItem(key);
  let cachedAt = toNumber(localStorage.getItem(tsKey));

  if (!raw) {
    raw = localStorage.getItem(CONTACTS_CACHE_KEY);
    cachedAt = toNumber(localStorage.getItem(CONTACTS_CACHE_TS_KEY));
  }

  if (!raw) return {};
  if (!isFresh(cachedAt, CONTACTS_CACHE_TTL_MS)) {
    clearContactsCache();
    return {};
  }
  return sanitizeContactsMap(safeParseJSON(raw, {}));
};

export const readContactsCacheList = () => flattenContactsCacheMap(readContactsCache());

export const writeContactsCache = (map) => {
  const key = scopedKey(CONTACTS_CACHE_KEY);
  const tsKey = scopedKey(CONTACTS_CACHE_TS_KEY);
  const now = Date.now().toString();
  const sanitizedMap = sanitizeContactsMap(map);
  const payload = JSON.stringify(sanitizedMap);
  const valueSaved = safeSetItem(key, payload);
  const tsSaved = valueSaved ? safeSetItem(tsKey, now) : false;

  if (!valueSaved || !tsSaved) {
    clearContactsCache();
    return;
  }

  if (key !== CONTACTS_CACHE_KEY) {
    try {
      localStorage.removeItem(CONTACTS_CACHE_KEY);
      localStorage.removeItem(CONTACTS_CACHE_TS_KEY);
    } catch (err) {
      console.warn('Não foi possível remover cache legado de contatos:', err);
    }
  }
};

export const writeContactsCacheList = (list, bucketKey = CONTACTS_CACHE_ALL_KEY) => {
  const current = readContactsCache();
  const next = {
    ...(current || {}),
    [bucketKey]: sanitizeContactsList(list),
  };
  writeContactsCache(next);
  return next[bucketKey] || [];
};

export const clearContactsCache = () => {
  try {
    const prefixes = [CONTACTS_CACHE_KEY, CONTACTS_CACHE_TS_KEY];
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (prefixes.some((p) => k === p || k.startsWith(`${p}::`))) {
        localStorage.removeItem(k);
      }
    }
  } catch (err) {
    console.warn('Não foi possível limpar o cache de contatos:', err);
  }
};

// ===== CLIENTS (lista de clientes) com escopo por empresa =====
export const readClientsCache = (options = {}) => {
  const { ttlMs = CLIENTS_CACHE_TTL_MS } = options ?? {};

  const key = scopedKey(CLIENTS_CACHE_KEY);
  const tsKey = scopedKey(CLIENTS_CACHE_TS_KEY);

  let raw = null;
  let cachedAt = null;

  try {
    raw = localStorage.getItem(key);
    cachedAt = toNumber(localStorage.getItem(tsKey));

    if (!raw) {
      raw = localStorage.getItem(CLIENTS_CACHE_KEY);
      cachedAt = toNumber(localStorage.getItem(CLIENTS_CACHE_TS_KEY));
    }
  } catch {
    raw = null;
    cachedAt = null;
  }

  if (!raw) {
    return [];
  }

  if (!isFresh(cachedAt, ttlMs)) {
    try {
      localStorage.removeItem(key);
      localStorage.removeItem(tsKey);
      if (key !== CLIENTS_CACHE_KEY) {
        localStorage.removeItem(CLIENTS_CACHE_KEY);
        localStorage.removeItem(CLIENTS_CACHE_TS_KEY);
      }
    } catch {
      // ignore cleanup errors
    }
    return [];
  }

  return safeParseJSON(raw, []);
};

export const writeClientsCache = (list) => {
  const key = scopedKey(CLIENTS_CACHE_KEY);
  const tsKey = scopedKey(CLIENTS_CACHE_TS_KEY);
  const now = Date.now().toString();
  // versão com escopo
  safeSetItem(key, JSON.stringify(list ?? []));
  safeSetItem(tsKey, now);
  // compat legado
  safeSetItem(CLIENTS_CACHE_KEY, JSON.stringify(list ?? []));
  safeSetItem(CLIENTS_CACHE_TS_KEY, now);
};

export const clearChatSlotsCache = () => {
  try {
    const keysToDelete = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CHAT_SLOTS_PREFIX)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach((key) => localStorage.removeItem(key));
  } catch (err) {
    console.warn('Não foi possível limpar o cache de slots:', err);
  }
};

export const clearCompanyScopedCaches = () => {
  clearAttendantCache();
  clearContactsCache();
  try {
    const prefixes = [CLIENTS_CACHE_KEY, CLIENTS_CACHE_TS_KEY];
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (prefixes.some((p) => k === p || k.startsWith(`${p}::`))) {
        localStorage.removeItem(k);
      }
    }
  } catch (err) {
    console.warn('Não foi possível limpar o cache de clientes:', err);
  }
  localStorage.removeItem('attendants');
  localStorage.removeItem('chat_contacts');
  localStorage.removeItem('chat_contacts_cached_at');
  localStorage.removeItem('chat_companies');
  localStorage.removeItem('chat_companies_cached_at');
  clearChatSlotsCache();
};

export const clearAllSessionCaches = () => {
  clearCompanyScopedCaches();

  let clearedAll = false;
  try {
    localStorage.clear();
    clearedAll = true;
  } catch (err) {
    console.warn('Não foi possível limpar todo o localStorage, limpando chaves conhecidas.', err);
  }

  if (!clearedAll) {
    try {
      localStorage.removeItem('attendant_id');
      localStorage.removeItem('company_id');
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
      localStorage.removeItem('role_name');
      localStorage.removeItem('role_id');
      localStorage.removeItem('chat_contacts');
      localStorage.removeItem('chat_contacts_cached_at');
      localStorage.removeItem('chat_companies');
      localStorage.removeItem('chat_companies_cached_at');
    } catch {
      // ignore cleanup errors
    }
  }

  try {
    sessionStorage.clear();
  } catch {
    // ignore
  }

  try {
    localStorage.removeItem(BUILD_CACHE_KEY);
  } catch {
    // ignore
  }

  clearBrowserCaches();
};

export const pruneExpiredCaches = () => {
  readAttendantCache();
  readContactsCache();
};

export const enforceFreshBuildCache = (buildId) => {
  if (!buildId) return;
  let needsReset = true;
  try {
    const stored = localStorage.getItem(BUILD_CACHE_KEY);
    if (stored && stored === buildId) {
      needsReset = false;
    }
  } catch {
    // Se não conseguir ler, força o reset abaixo
    needsReset = true;
  }

  if (!needsReset) return;

  // Guarda apenas o estado de autenticação para não forçar logout em novo build
  let preservedAuth = null;
  try {
    preservedAuth = {
      token: localStorage.getItem('token') || localStorage.getItem('authToken') || null,
      attendantId: localStorage.getItem('attendant_id') || null,
      companyId: localStorage.getItem('company_id') || null,
      roleName: localStorage.getItem('role_name') || null,
      roleId: localStorage.getItem('role_id') || null,
    };
  } catch {
    preservedAuth = null;
  }

  // Build mudou: zera caches e estados voláteis, mas repõe credenciais
  clearAllSessionCaches();

  try {
    if (preservedAuth?.token) {
      localStorage.setItem('token', preservedAuth.token);
      localStorage.setItem('authToken', preservedAuth.token);
    }
    if (preservedAuth?.attendantId) localStorage.setItem('attendant_id', preservedAuth.attendantId);
    if (preservedAuth?.companyId) localStorage.setItem('company_id', preservedAuth.companyId);
    if (preservedAuth?.roleName) localStorage.setItem('role_name', preservedAuth.roleName);
    if (preservedAuth?.roleId) localStorage.setItem('role_id', preservedAuth.roleId);
  } catch {
    // Se não conseguir regravar, deixa seguir para reautenticação natural
  }

  try {
    localStorage.setItem(BUILD_CACHE_KEY, buildId);
  } catch {
    // ignore
  }
};

export {
  ATTENDANT_CACHE_KEY,
  ATTENDANT_CACHE_TS_KEY,
  ATTENDANT_CACHE_TTL_MS,
  CONTACTS_CACHE_KEY,
  CONTACTS_CACHE_TS_KEY,
  CONTACTS_CACHE_TTL_MS,
  CLIENTS_CACHE_KEY,
  CLIENTS_CACHE_TS_KEY,
  CLIENTS_CACHE_TTL_MS,
};
